import { FC, useEffect, useRef, useState } from 'react';
import Loader from '../loader/Loader';
import Modal from '../modal/Modal';
import LogAccountTankMaintenanceModal from './LogAccountTankMaintenanceModal';
import SortedTable from '../sorted-table/SortedTable';
import TankDueBadge from './TankDueBadge';
import {
  getAccountTankMaintenance,
  getAccountTankMaintenanceHistory,
  getAccountTankMaintenanceSettings,
} from '../../api/accountTankMaintenanceApi';
import { showToast } from '../../services/toast.service';
import { AccountTankMaintenance, AccountTankMaintenanceHistory } from '../../types/interfaces/accountTankMaintenance.interfaces';
import { PagedResponse } from '../../types/interfaces';
import { usDateFormatMMDDYYYY } from '../../utils/commonUtils';
import { getDaysUntilDue, getDueStatusFromNextDueDate } from '../../utils/tankMaintenance.utils';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import './tankMaintenance.scss';

type AccountTankMaintenancePanelProps = {
  tankId: number;
  tankName: string;
  siteName?: string;
  canEdit: boolean;
  refreshKey?: number;
  onChanged?: () => void;
};

const HISTORY_PAGE_SIZE = 10;

const AccountTankMaintenancePanel: FC<AccountTankMaintenancePanelProps> = ({
  tankId,
  tankName,
  siteName,
  canEdit,
  refreshKey = 0,
  onChanged,
}): JSX.Element => {
  const [maintenance, setMaintenance] = useState<AccountTankMaintenance>();
  const [history, setHistory] = useState<AccountTankMaintenanceHistory[]>([]);
  const [historyTotal, setHistoryTotal] = useState<number>(0);
  const [historyOffset, setHistoryOffset] = useState<number>(0);
  const [reminderAdvanceDays, setReminderAdvanceDays] = useState<number>(3);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [showLogModal, setShowLogModal] = useState<boolean>(false);
  const [panelRefresh, setPanelRefresh] = useState<number>(0);
  const loadRequestId = useRef(0);
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data: setting } = await getAccountTankMaintenanceSettings();
        setReminderAdvanceDays(setting.reminderAdvanceDays ?? 3);
        setIsEnabled(setting.isEnabled);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };
    loadSettings();
  }, [refreshKey]);

  useEffect(() => {
    const requestId = ++loadRequestId.current;
    const load = async () => {
      if (hasLoadedOnce.current) {
        setRefreshing(true);
      } else {
        setInitialLoading(true);
      }

      try {
        const [{ data: tankMaintenance }, { data: historyResponse }] = await Promise.all([
          getAccountTankMaintenance(tankId),
          getAccountTankMaintenanceHistory(tankId, { limit: HISTORY_PAGE_SIZE, offset: 0 }),
        ]);

        if (requestId !== loadRequestId.current) {
          return;
        }

        setMaintenance(tankMaintenance);

        if (Array.isArray(historyResponse)) {
          setHistory(historyResponse);
          setHistoryTotal(historyResponse.length);
        } else {
          const paged = historyResponse as PagedResponse<AccountTankMaintenanceHistory>;
          setHistory(paged.result);
          setHistoryTotal(paged.total);
        }
        setHistoryOffset(0);
        hasLoadedOnce.current = true;
      } catch {
        if (requestId !== loadRequestId.current) {
          return;
        }
        setMaintenance(undefined);
        setHistory([]);
        setHistoryTotal(0);
        setHistoryOffset(0);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        if (requestId === loadRequestId.current) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    };
    load();
  }, [tankId, refreshKey, panelRefresh]);

  const loadMoreHistory = async () => {
    const nextOffset = historyOffset + HISTORY_PAGE_SIZE;
    const requestId = loadRequestId.current;
    try {
      const { data } = await getAccountTankMaintenanceHistory(tankId, { limit: HISTORY_PAGE_SIZE, offset: nextOffset });
      if (requestId !== loadRequestId.current) {
        return;
      }
      if (Array.isArray(data)) {
        setHistory(prev => [...prev, ...data]);
        setHistoryOffset(nextOffset);
      } else {
        const paged = data as PagedResponse<AccountTankMaintenanceHistory>;
        setHistory(prev => [...prev, ...paged.result]);
        setHistoryTotal(paged.total);
        setHistoryOffset(nextOffset);
      }
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const dueStatus = getDueStatusFromNextDueDate(
    maintenance?.nextDueDate,
    reminderAdvanceDays,
    isEnabled && !!maintenance?.isActive && maintenance?.isMaintenanceEnabled !== false,
  );
  const daysUntilDue = dueStatus !== 'disabled' ? getDaysUntilDue(maintenance?.nextDueDate) : undefined;

  const historyTableData = history.map((entry: AccountTankMaintenanceHistory) => [
    { content: usDateFormatMMDDYYYY(new Date(entry.performedDate)), sortValue: new Date(entry.performedDate) },
    { content: <span title={entry.initials}>{entry.initials || '--'}</span>, sortValue: entry.initials || '' },
    { content: entry.lN2Level != null ? entry.lN2Level : '--', sortValue: entry.lN2Level ?? -1 },
    { content: usDateFormatMMDDYYYY(new Date(entry.nextDueDateCalculated)), sortValue: new Date(entry.nextDueDateCalculated) },
    {
      content: (
        <span className="history-notes-cell" title={entry.notes?.trim() || undefined}>
          {entry.notes?.trim() ? entry.notes : '--'}
        </span>
      ),
      sortValue: entry.notes || '',
    },
  ]);

  const statusValue = () => {
    if (dueStatus === 'ok') {
      return <span className="status-ok">{LABEL_CONSTANTS.UP_TO_DATE}</span>;
    }
    if (dueStatus === 'disabled') {
      return <span className="status-disabled">{LABEL_CONSTANTS.MAINTENANCE_DISABLED}</span>;
    }
    return <TankDueBadge status={dueStatus} daysUntilDue={daysUntilDue} />;
  };

  return (
    <div className={`account-tank-maintenance-panel${refreshing ? ' is-refreshing' : ''}`}>
      {showLogModal && (
        <Modal isOpen={showLogModal} onClose={() => setShowLogModal(false)}>
          <LogAccountTankMaintenanceModal
            tankId={tankId}
            tankName={tankName}
            siteName={siteName}
            maintenanceTypeId={maintenance?.maintenanceTypeId}
            close={() => setShowLogModal(false)}
            onLogged={() => {
              setPanelRefresh(prev => prev + 1);
              onChanged?.();
            }}
          />
        </Modal>
      )}

      <div className="panel-header">
        <div className="panel-title-group">
          <h3>{LABEL_CONSTANTS.TANK_MAINTENANCE}</h3>
          <span className="panel-subtitle">
            {tankName}
            {siteName ? ` · ${siteName}` : ''}
          </span>
        </div>
        {canEdit && isEnabled && maintenance?.isActive && maintenance?.isMaintenanceEnabled !== false && (
          <div className="panel-actions">
            <button type="button" className="green button tank-maintenance-log-btn" onClick={() => setShowLogModal(true)}>
              {LABEL_CONSTANTS.LOG_MAINTENANCE}
            </button>
          </div>
        )}
      </div>

      {initialLoading ? (
        <Loader loaderSize="small" simple />
      ) : (
        <>
          <div className="panel-status">
            <div className="status-card">
              <span className="status-label">{LABEL_CONSTANTS.STATUS}</span>
              <div className="status-value">{statusValue()}</div>
            </div>
            <div className="status-card">
              <span className="status-label">{LABEL_CONSTANTS.LAST_PERFORMED}</span>
              <div className="status-value">
                {maintenance?.lastPerformedDate ? usDateFormatMMDDYYYY(new Date(maintenance.lastPerformedDate)) : '--'}
              </div>
            </div>
            <div className="status-card">
              <span className="status-label">{LABEL_CONSTANTS.NEXT_DUE}</span>
              <div className="status-value">
                {dueStatus === 'disabled' || !maintenance?.nextDueDate
                  ? '--'
                  : usDateFormatMMDDYYYY(new Date(maintenance.nextDueDate))}
              </div>
            </div>
            <div className="status-card">
              <span className="status-label">{LABEL_CONSTANTS.LN2_LEVEL}</span>
              <div className="status-value">{maintenance?.lN2Level != null ? maintenance.lN2Level : '--'}</div>
            </div>
          </div>

          <div className="panel-history">
            <div className="history-header">
              <h4>{LABEL_CONSTANTS.MAINTENANCE_HISTORY}</h4>
            </div>

            <div className="desk-maintenance-history">
              <SortedTable
                headers={[
                  { displayName: LABEL_CONSTANTS.DATE, sortable: true },
                  { displayName: LABEL_CONSTANTS.INITIALS },
                  { displayName: LABEL_CONSTANTS.LN2_LEVEL, sortable: true },
                  { displayName: LABEL_CONSTANTS.NEXT_DUE, sortable: true },
                  { displayName: LABEL_CONSTANTS.NOTES },
                ]}
                data={historyTableData}
                height={256}
                loading={refreshing}
                noResultsMessage={LABEL_CONSTANTS.NO_MAINTENANCE_HISTORY}
              />
            </div>

            {/** Mobile View */}
            <div className="xs-maintenance-history">
              {history.length === 0 && !refreshing ? (
                <div className="result-item">{LABEL_CONSTANTS.NO_MAINTENANCE_HISTORY}</div>
              ) : (
                history.map(entry => (
                  <div className="card max-width" key={entry.accountTankMaintenanceHistoryId}>
                    <div className="maintenance-history-card">
                      <div className="maintenance-history-content">
                        <div className="maintenance-info">
                          <div className="maintenance-content">
                            <label>{`${LABEL_CONSTANTS.DATE}: `}</label>
                            <label>{usDateFormatMMDDYYYY(new Date(entry.performedDate)) || '--'}</label>
                          </div>
                          <div className="maintenance-content">
                            <label>{`${LABEL_CONSTANTS.INITIALS}: `}</label>
                            <label>{entry.initials || '--'}</label>
                          </div>
                          <div className="maintenance-content">
                            <label>{`${LABEL_CONSTANTS.LN2_LEVEL}: `}</label>
                            <label>{entry.lN2Level != null ? entry.lN2Level : '--'}</label>
                          </div>
                        </div>
                        <div className="maintenance-info">
                          <div className="maintenance-content">
                            <label>{`${LABEL_CONSTANTS.NEXT_DUE}: `}</label>
                            <label>{usDateFormatMMDDYYYY(new Date(entry.nextDueDateCalculated)) || '--'}</label>
                          </div>
                          <div className="maintenance-content maintenance-content--notes">
                            <label>{`${LABEL_CONSTANTS.NOTES}: `}</label>
                            <label>{entry.notes?.trim() ? entry.notes : '--'}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              {refreshing && <Loader loaderSize="small" simple />}
            </div>

            {!refreshing && history.length < historyTotal && (
              <div className="history-load-more">
                <button type="button" className="small green inverted button" onClick={loadMoreHistory}>
                  {BUTTON_CONSTANTS.LOAD_MORE}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountTankMaintenancePanel;
